// NodeSharePrintModal.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { normalizeAncestryPath } from 'utilities/nodeAncestry';
import { openPrintDocument } from 'utilities/Print';
import { renderQRCodeSvgToPngDataUrl } from 'utilities/QRCode';
import {
  buildQrPrintAncestorLines,
  getQrPrintTypography,
} from 'nodeshare/utils/qrPrintAncestors';
import { buildQrPrintFieldLines } from 'nodeshare/utils/qrPrintFields';

// Services
import { getNodeBreadcrumbFields } from 'services/nodes/NodeReadService';

// CSS
import './NodeSharePrintModal.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';

const NODE_SHARE_PRINT_DOCUMENT_STYLES = `
  * {
    box-sizing: border-box;
  }

  html,
  body {
    background: #ffffff;
    color: #000000;
    font-family: Arial, sans-serif;
    margin: 0;
    padding: 0;
  }

  .node-share-print-modal-container {
    padding: 0 !important;
  }

  .node-share-print-modal-sheet {
    align-items: center;
    background: #ffffff;
    color: #000000;
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 0 auto;
    max-width: 420px;
    padding: 32px 24px;
    width: 100%;
  }

  .node-share-print-modal-label,
  .node-share-print-modal-key,
  .node-share-print-modal-ancestor,
  .node-share-print-modal-title,
  .node-share-print-modal-field,
  .node-share-print-modal-field-label,
  .node-share-print-modal-field-value {
    color: #000000;
  }

  .node-share-print-modal-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .node-share-print-modal-qr-shell {
    align-items: center;
    background: #ffffff;
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .node-share-print-modal-svg {
    display: flex;
    height: 280px;
    width: 280px;
  }

  .node-share-print-modal-svg img,
  .node-share-print-modal-svg svg {
    display: block;
    height: 100%;
    width: 100%;
  }

  .node-share-print-modal-ancestors {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
  }

  .node-share-print-modal-ancestor {
    font-weight: 700;
    line-height: 1.25;
    text-align: center;
    word-break: break-word;
  }

  .node-share-print-modal-title {
    font-weight: 700;
    line-height: 1.25;
    text-align: center;
    word-break: break-word;
  }

  .node-share-print-modal-fields {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
  }

  .node-share-print-modal-field {
    line-height: 1.35;
    text-align: center;
    word-break: break-word;
  }

  .node-share-print-modal-field-label {
    font-weight: 700;
  }

  .node-share-print-modal-key {
    font-size: 12px;
    letter-spacing: 0.04em;
    line-height: 1.4;
    text-align: center;
    word-break: break-all;
  }

  @page {
    margin: 0.5in;
  }
`;

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * buildQrPrintAncestorsMarkup
 *
 * @param {{ key: string, title: string }[]} ancestorLines
 * @param {number[]} ancestorSizesPx
 * @returns {string}
 */
function buildQrPrintAncestorsMarkup(ancestorLines, ancestorSizesPx) {
    if (!Array.isArray(ancestorLines) || !ancestorLines.length) return '';

    const rows = ancestorLines.map((line, index) => {
        const fontSize = Array.isArray(ancestorSizesPx)
            ? ancestorSizesPx[index]
            : null;
        const sizeStyle = Number.isFinite(fontSize)
            ? ` style="font-size: ${fontSize}px;"`
            : '';
        return `
        <div class="node-share-print-modal-ancestor"${sizeStyle}>
          ${escapeHtml(line.title)}
        </div>
      `;
    }).join('');

    return `<div class="node-share-print-modal-ancestors">${rows}</div>`;
}

/**
 * buildQrPrintFieldsMarkup
 *
 * @param {{ key: string, label: string, value: string }[]} fieldLines
 * @param {number} fieldSizePx
 * @returns {string}
 */
function buildQrPrintFieldsMarkup(fieldLines, fieldSizePx) {
    if (!Array.isArray(fieldLines) || !fieldLines.length) return '';

    const sizeStyle = Number.isFinite(fieldSizePx)
        ? ` style="font-size: ${fieldSizePx}px;"`
        : '';

    const rows = fieldLines.map((line) => `
        <div class="node-share-print-modal-field"${sizeStyle}>
          <span class="node-share-print-modal-field-label">${escapeHtml(line.label)}: </span>
          <span class="node-share-print-modal-field-value">${escapeHtml(line.value)}</span>
        </div>
      `).join('');

    return `<div class="node-share-print-modal-fields">${rows}</div>`;
}

/**
 * NodeSharePrintModal
 *
 * Renders a focused QR print preview for the QR Code modal.
 *
 * @param {boolean} isOpen - Whether the print preview is open.
 * @param {object} node - Node whose QR code is being printed.
 * @param {function} onClose - Called when the modal should close.
 * @param {string} svgMarkup - Serialized SVG markup for the QR code.
 *
 * @returns {ReactNode} The rendered share print preview modal.
 */
const NodeSharePrintModal = ({ isOpen, node, onClose, svgMarkup }) => {

    // ----- CONTEXT ----- //

    const { getType } = useNodeTypes();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- MEMOS ----- //

    const printableTitle = useMemo(() => String(node?.title || '').trim() || 'Untitled', [node?.title]);

    const typeDef = useMemo(() => {
        const typeKey = String(node?.type || '').trim();
        return typeKey ? getType(typeKey) : null;
    }, [getType, node?.type]);

    const printAncestorsOnQr = typeDef?.printAncestorsOnQr === true;

    const ancestryPath = useMemo(
        () => (printAncestorsOnQr ? normalizeAncestryPath(node?.ancestryPath) : []),
        [node?.ancestryPath, printAncestorsOnQr],
    );

    const ancestryKeys = useMemo(
        () => ancestryPath.map((entry) => entry.key),
        [ancestryPath],
    );

    const fieldLines = useMemo(
        () => buildQrPrintFieldLines(node, typeDef),
        [node, typeDef],
    );

    // ----- STATE ----- //

    // Hydrated titles for ancestry keys (same source as breadcrumbs).
    const [titleByKey, setTitleByKey] = useState(() => new Map());

    // ----- EFFECTS ----- //

    // Batch-load ancestor titles when the type opts into QR ancestry print.
    useEffect(() => {
        let cancelled = false;

        if (!isOpen || !printAncestorsOnQr || !ancestryKeys.length) {
            setTitleByKey(new Map());
            return undefined;
        }

        getNodeBreadcrumbFields(ancestryKeys)
            .then((map) => {
                if (!cancelled) setTitleByKey(map || new Map());
            })
            .catch(() => {
                if (!cancelled) setTitleByKey(new Map());
            });

        return () => {
            cancelled = true;
        };
    }, [ancestryKeys, isOpen, printAncestorsOnQr]);

    // ----- MEMOS ----- //

    const ancestorLines = useMemo(
        () => (
            printAncestorsOnQr
                ? buildQrPrintAncestorLines(ancestryPath, titleByKey)
                : []
        ),
        [ancestryPath, printAncestorsOnQr, titleByKey],
    );

    const typography = useMemo(
        () => getQrPrintTypography(ancestorLines.length),
        [ancestorLines.length],
    );

    // ----- HANDLERS ----- //

    /**
     * Handles print.
     */
    const handlePrint = useCallback(async () => {
        if (typeof window === 'undefined') return;

        const printableImageSrc = await renderQRCodeSvgToPngDataUrl(svgMarkup, { size: 560 });
        const printableQRCodeMarkup = printableImageSrc
            ? `
        <div class="node-share-print-modal-svg">
          <img src="${printableImageSrc}" alt="QR code for ${escapeHtml(printableTitle)}" />
        </div>
      `
            : `
        <div class="node-share-print-modal-svg">
          ${svgMarkup}
        </div>
      `;

        const printableMarkup = `
      <div class="node-share-print-modal-sheet">
        <div class="node-share-print-modal-qr-shell">
          ${printableQRCodeMarkup}
        </div>
        ${buildQrPrintAncestorsMarkup(ancestorLines, typography.ancestorSizesPx)}
        <div class="node-share-print-modal-title" style="font-size: ${typography.titleSizePx}px;">
          ${escapeHtml(printableTitle)}
        </div>
        ${buildQrPrintFieldsMarkup(fieldLines, typography.fieldSizePx)}
      </div>
    `;

        openPrintDocument({
            bodyMarkup: printableMarkup,
          onPrintTriggered: onClose,
            styles: NODE_SHARE_PRINT_DOCUMENT_STYLES,
            title: printableTitle,
        });
      }, [
        ancestorLines,
        fieldLines,
        onClose,
        printableTitle,
        svgMarkup,
        typography.ancestorSizesPx,
        typography.fieldSizePx,
        typography.titleSizePx,
      ]);

    // ----- RENDER ----- //

    return (
        <Modal
          title="QR Print Preview"
          isOpen={isOpen}
          width={isMobile ? '100%' : 520}
          maxHeight="90vh"
          onClose={onClose}
        >
            <ModalForm>
                <div className="node-share-print-modal-container">

                    {/* SHEET */}
                    <div className="node-share-print-modal-sheet">

                        {/* QR SHELL */}
                        <div className="node-share-print-modal-qr-shell">
                            {/* QR SVG */}
                            <div
                              className="node-share-print-modal-svg"
                              dangerouslySetInnerHTML={{ __html: svgMarkup }}
                            />
                        </div>

                        {/* ANCESTORS */}
                        {ancestorLines.length ? (
                            <div className="node-share-print-modal-ancestors">
                                {ancestorLines.map((line, index) => (
                                    <div
                                      className="node-share-print-modal-ancestor"
                                      key={line.key}
                                      style={{
                                        fontSize: `${typography.ancestorSizesPx[index]}px`,
                                      }}
                                    >
                                        {line.title}
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        {/* TITLE */}
                        <div
                          className="node-share-print-modal-title"
                          style={{
                            fontSize: `${typography.titleSizePx}px`,
                          }}
                        >
                            {printableTitle}
                        </div>

                        {/* FIELDS */}
                        {fieldLines.length ? (
                            <div className="node-share-print-modal-fields">
                                {fieldLines.map((line) => (
                                    <div
                                      className="node-share-print-modal-field"
                                      key={line.key}
                                      style={{
                                        fontSize: `${typography.fieldSizePx}px`,
                                      }}
                                    >
                                        <span className="node-share-print-modal-field-label">
                                            {line.label}
                                            :
                                            {' '}
                                        </span>
                                        <span className="node-share-print-modal-field-value">
                                            {line.value}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : null}

                    </div>
                </div>
            </ModalForm>

            <ModalButtons>
                <Button
                  label="Print"
                  onClick={handlePrint}
                />
            </ModalButtons>
        </Modal>
    );
};

// Dev Tools
NodeSharePrintModal.displayName = 'NodeSharePrintModal';

export default NodeSharePrintModal;
